import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import FormControlDefault from "../../../../../components/input/FormControlDefault";
import { primary, textSecondary, white } from "../../../../../utils/colors";
import FormControlCountry from "../../../../../components/input/FormControlCountry";
import FormControlAddressFull from "../../../../../components/input/FormControlAddressFull";
import FormControlPhone from "../../../../../components/input/FormControlPhone";
import SaveButtons from "./SaveButtons";
import { useDispatch, useSelector } from "react-redux";
import { TENANT_UPDATE_CLINIC_DETAILS_RESET } from "../../../../../constants/tenantConstants";
import { updateTenantClinicDetailsAction } from "../../../../../actions/tenantActions";

const ClinicDetails = ({ slug, tenant }) => {
  // GENERAL
  // const { slug } = useParams();
  const { t } = useTranslation();
  const toast = useToast();

  // COLORS
  const { whiteMode, backgroundLightMode, textPrimaryMode, newBorderMode } =
    useThemeColors();

  // STATE
  const [showButtons, setShowButtons] = useState(false);

  const [clinicName, setClinicName] = useState(tenant?.clinicName || "");
  const [registrationEmail, setRegistrationEmail] = useState(
    tenant?.registrationEmail || ""
  );
  const [supportEmail, setSupportEmail] = useState(tenant?.supportEmail || "");
  const [phone, setPhone] = useState(tenant?.phone || "");
  const [website, setWebsite] = useState(tenant?.website || "");

  const [address, setAddress] = useState(() => ({
    street: tenant?.address?.street || "",
    city: tenant?.address?.city || "",
    country: tenant?.address?.country || "",
    zip: tenant?.address?.zip || "",
    state: tenant?.address?.state || "",
    display: tenant?.address
      ? `${tenant.address.street}, ${tenant.address.city}, ${tenant.address.zip}`
      : "",
  }));

  const [manualAddressVisible, setManualAddressVisible] = useState(false);

  // REDUX
  const dispatch = useDispatch();

  const tenantUpdateClinicDetails = useSelector(
    (state) => state.tenantUpdateClinicDetails
  );
  const {
    loading: loadingUpdateClinicDetails,
    error: errorUpdateClinicDetails,
    success: successUpdateClinicDetails,
  } = tenantUpdateClinicDetails;

  // HANDLERS
  const updateClinicDetailsHandler = () => {
    console.log("Clinic details to be sent:", {
      clinicName,
      supportEmail,
      phone,
      address,
    }); // ✅ Debugging log

    const clinicDetails = {
      clinicName,
      supportEmail,
      phone,
      address,
    };

    dispatch(
      updateTenantClinicDetailsAction(slug, tenant.tenantID, clinicDetails)
    );
  };

  const toggleManualAddressVisible = () => {
    setManualAddressVisible(!manualAddressVisible);
  };

  // Handler for when an address is selected
  const handleAddressSelect = (selectedAddress) => {
    setAddress({
      street: selectedAddress.street,
      city: selectedAddress.city,
      country: selectedAddress.country,
      zip: selectedAddress.zip,
      state: selectedAddress.state,
      display: `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.zip}`,
    });
  };

  // USE EFFECT

  const normalizeValue = (value) =>
    value === undefined || value === null ? "" : String(value).trim();

  const normalizePhone = (phone) =>
    phone === undefined || phone === null || phone === "" || phone === "+370"
      ? ""
      : phone;

  useEffect(() => {
    // console.log("Checking for changes...");

    const hasAddressChanged =
      normalizeValue(tenant?.address?.street) !==
        normalizeValue(address.street) ||
      normalizeValue(tenant?.address?.city) !== normalizeValue(address.city) ||
      normalizeValue(tenant?.address?.zip) !== normalizeValue(address.zip) ||
      normalizeValue(tenant?.address?.country) !==
        normalizeValue(address.country) ||
      normalizeValue(tenant?.address?.state) !== normalizeValue(address.state);

    // Fix phone comparison: Normalize phone numbers properly
    const hasPhoneChanged =
      normalizePhone(tenant?.phone) !== normalizePhone(phone);

    // console.log("Has address changed?", hasAddressChanged);
    // console.log("Has phone changed?", hasPhoneChanged);

    if (
      normalizeValue(clinicName) !== normalizeValue(tenant?.clinicName) ||
      normalizeValue(registrationEmail) !==
        normalizeValue(tenant?.registrationEmail) ||
      normalizeValue(supportEmail) !== normalizeValue(tenant?.supportEmail) ||
      hasAddressChanged ||
      hasPhoneChanged
    ) {
      // console.log("Setting showButtons to TRUE");
      setShowButtons(true);
    } else {
      // console.log("Setting showButtons to FALSE");
      setShowButtons(false);
    }
  }, [clinicName, registrationEmail, phone, supportEmail, address, tenant]);

  useEffect(() => {
    if (successUpdateClinicDetails) {
      toast({
        title: t("toasts.clinicDetailsUpdated"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowButtons(false);
    } else if (errorUpdateClinicDetails) {
      toast({
        title: t("toasts.clinicDetailsUpdatedError"),
        status: "error",
        duration: 3000,

        isClosable: true,
      });
    }

    dispatch({ type: TENANT_UPDATE_CLINIC_DETAILS_RESET });
  }, [successUpdateClinicDetails, errorUpdateClinicDetails]);

  // LOGS
  // console.log("tenant", tenant);
  // console.log("showButtons", showButtons);
  // console.log("clinicName", clinicName);
  // console.log("registrationEmail", registrationEmail);
  // console.log("supportEmail", supportEmail);
  // console.log("phone", phone);
  // console.log("address", address);

  return (
    <Box
      w="100%"
      p={["1.5rem 0rem 1.5rem", "3rem 0rem 3rem"]}
      borderBottom={`1px solid ${newBorderMode}`}
    >
      <Flex
        w="100%"
        justify="space-between"
        gap={["1.5rem", "2rem"]}
        direction={["column", "row"]}
      >
        {/* LEFT START */}
        <Flex direction="column" gap={2} w={["100%", "40%"]}>
          <Heading fontSize="1rem" fontWeight="600" p={["0 1.5rem", "initial"]}>
            {t("clinicSettings.clinicDetails")}
          </Heading>
          <Text
            color={textSecondary}
            fontSize="sm"
            fontWeight="500"
            p={["0 1.5rem", "initial"]}
          >
            {t("clinicSettings.clinicDetailsDescription")}
          </Text>
        </Flex>
        {/* LEFT END */}

        {/* RIGHT START */}
        <Flex
          w={["100%", "800px"]}
          direction="column"
          justify="flex-start"
          align="flex-start"
        >
          <Flex
            direction="column"
            w="100%"
            bg={backgroundLightMode}
            borderRadius="1rem"
            p={["1.25rem 1.5rem 1.25rem", "1.25rem 1.5rem 1.25rem"]}
          >
            <FormControlDefault
              // autoFocus
              type="text"
              size="lg"
              labelFontSize="0.8rem"
              isRequired
              label={t("form.clinicName")}
              value={clinicName}
              placeholder={t("placeholder.clinicName")}
              onChange={(e) => {
                setClinicName(e.target.value);
              }}
              w={["100%", "100%", "100%"]}
            />

            <Flex w="100%">
              <FormControlDefault
                type="email"
                size="lg"
                labelFontSize="0.8rem"
                isRequired
                label={t("form.registrationEmail")}
                value={registrationEmail}
                placeholder={t("placeholder.registrationEmail")}
                onChange={(e) => {
                  setRegistrationEmail(e.target.value);
                }}
                // isError={isEmailValidated}
                w="100%"
                errorMessage={t("formError.registrationEmailRequired")}
                isDisabled
              />
            </Flex>

            <Flex
              gap={["0rem", "2rem", "2rem"]}
              w="100%"
              direction={["column", "row", "row"]}
            >
              <FormControlDefault
                type="email"
                size="lg"
                labelFontSize="0.8rem"
                isRequired
                label={t("form.supportEmail")}
                value={supportEmail}
                placeholder={t("placeholder.supportEmail")}
                onChange={(e) => {
                  setSupportEmail(e.target.value);
                }}
                // isError={isEmailValidated}
                w={["100%", "50%", "50%"]}
                errorMessage={t("formError.supportEmailRequired")}
              />

              <FormControlPhone
                label={t("form.phone")}
                size="lg"
                labelFontSize="0.8rem"
                value={phone}
                onChange={(newPhone) => {
                  setPhone(newPhone);
                }}
                isRequired={true}
                w={["100%", "50%", "50%"]}
                defaultCountry="lt"
                zIndex={93}
              />
            </Flex>

            {!manualAddressVisible && (
              <Flex direction="column" w="100%" p="0rem">
                <FormControlAddressFull
                  label={t("form.address")}
                  size="lg"
                  labelFontSize="0.8rem"
                  placeholder={t("placeholder.address")}
                  onAddressSelect={handleAddressSelect}
                  isRequired={true}
                  currentValue={address.display}
                  w="100%"
                  zIndex={92}
                />
                <Flex align="center" justify="center" gap="1rem" p="0.5rem 0 0">
                  <Flex
                    position="relative"
                    m="0rem 0 0"
                    justify="flex-end"
                    w="100%"
                  >
                    <Button
                      bg="transparent"
                      onClick={toggleManualAddressVisible}
                      color={primary}
                      fontSize="xs"
                      size="xs"
                      fontWeight="500"
                      p="0.25rem 0.75rem"
                      borderRadius="10rem"
                      opacity="1"
                      transition="all 0.3s"
                      _hover={{
                        bg: white,
                        opacity: "1",
                      }}
                    >
                      {t("onboarding.enterAddressManual")}
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            )}

            {manualAddressVisible && (
              <>
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  p="0rem"
                >
                  <HStack gap={4} w="100%">
                    <FormControlDefault
                      type="text"
                      size="lg"
                      labelFontSize="0.8rem"
                      isRequired={true}
                      label={t("form.street")}
                      value={address.street}
                      placeholder={t("placeholder.street")}
                      onChange={(e) =>
                        setAddress({
                          ...address,
                          street: e.target.value,
                        })
                      }
                      w={["100%", "50%", "50%"]}
                    />

                    <FormControlDefault
                      type="text"
                      isRequired={true}
                      size="lg"
                      labelFontSize="0.8rem"
                      label={t("form.zip")}
                      value={address.zip}
                      placeholder={t("placeholder.zip")}
                      onChange={(e) =>
                        setAddress({ ...address, zip: e.target.value })
                      }
                      w={["100%", "50%", "50%"]}
                    />
                  </HStack>

                  <HStack gap={4} w="100%">
                    <FormControlDefault
                      type="text"
                      isRequired={true}
                      size="lg"
                      labelFontSize="0.8rem"
                      label={t("form.city")}
                      value={address.city}
                      placeholder={t("placeholder.city")}
                      onChange={(e) =>
                        setAddress({ ...address, city: e.target.value })
                      }
                      w={["100%", "50%", "50%"]}
                      errorMessage={t("formError.cityRequired")}
                    />

                    <FormControlCountry
                      label={t("form.country")}
                      size="lg"
                      labelFontSize="0.8rem"
                      value={address.country}
                      isRequired={true}
                      onChange={(selectedOption) => {
                        setAddress(
                          Object.assign({}, address, {
                            country: selectedOption.value,
                          })
                        );
                      }}
                      w={["100%", "50%", "50%"]}
                      errorMessage={t("formError.countryRequired")}
                    />
                  </HStack>
                  <Flex align="center" justify="center" gap="1rem">
                    <Flex position="relative" m="0rem 0 0" justify="center">
                      <Button
                        bg="transparent)"
                        onClick={toggleManualAddressVisible}
                        color={primary}
                        variant="link"
                        fontSize="xs"
                        fontWeight="500"
                        p="0.25rem 0.75rem"
                        borderRadius="10rem"
                        _hover={{
                          bg: white,
                        }}
                      >
                        {t("onboarding.enterAddressGoogle")}
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
        {/* RIGHT END */}
      </Flex>

      {/* BUTTONS */}
      {showButtons && (
        <SaveButtons
          onClose={() => {
            setShowButtons(false);
            setClinicName(tenant?.clinicName || "");
            setRegistrationEmail(tenant?.registrationEmail || "");
            setSupportEmail(tenant?.supportEmail || "");
            setPhone(tenant?.phone || "");
            setWebsite(tenant?.website || "");
            setAddress({
              street: tenant?.address?.street || "",
              city: tenant?.address?.city || "",
              country: tenant?.address?.country || "",
              zip: tenant?.address?.zip || "",
              state: tenant?.address?.state || "",
              display: tenant?.address
                ? `${tenant.address.street}, ${tenant.address.city}, ${tenant.address.zip}`
                : "",
            });
          }}
          onClick={updateClinicDetailsHandler}
          loading
        />
      )}
    </Box>
  );
};

export default ClinicDetails;
